import { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useCanonical } from '@/hooks/useCanonical';
import { motion } from 'framer-motion';
import { MapPin, Phone, ArrowRight, CheckCircle2, Clock, Car, Train, Bus, Star, Users, Shield, Sparkles, Navigation, ParkingCircle, Route } from 'lucide-react';
import { cities } from '@/data/cities';
import { services, PLANITY_URL } from '@/data/services';
import { blogArticles } from '@/data/blog-articles';
import BlogCard from '@/components/blog/BlogCard';
import CTASection from '@/components/CTASection';
import ContactSection from '@/components/ContactSection';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const VilleDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const city = cities.find((c) => c.slug === slug);

  useCanonical(`/ville/${slug}`);

  useEffect(() => {
    if (city) {
      document.title = `Institut beauté ${city.prefix} ${city.name} (93) — Body by CC Sevran`;
      document.querySelector('meta[name="description"]')?.setAttribute(
        'content',
        `Body by CC, institut de beauté et bien-être à ${city.transport.distance} de ${city.name}. Soins visage, HIFU, épilation laser peau noire, pressothérapie, lipolaser. ${city.transport.duration}. Prenez RDV à Sevran (93).`
      );

      // FAQPage JSON-LD
      const existingLd = document.getElementById('ville-faqpage-jsonld');
      if (existingLd) existingLd.remove();

      const faqQuestions = [
        { q: `Quels soins proposez-vous près de ${city.name} ?`, a: `Body by CC propose soins visage, HIFU, épilation laser Nd:YAG, pressothérapie, lipolaser, manucure, blanchiment dentaire et massages à seulement ${city.transport.distance} de ${city.name}.` },
        { q: `Comment venir depuis ${city.name} ?`, a: `En voiture (${city.transport.duration}): ${city.transport.car}${city.transport.rer ? '. En RER: ' + city.transport.rer : ''}${city.transport.bus ? '. En bus: ' + city.transport.bus : ''}.` },
        { q: 'Les soins sont-ils adaptés aux peaux noires et métissées ?', a: 'Oui, c\'est notre spécialité. Carine Tchuente maîtrise les spécificités des peaux à phototypes foncés : laser Nd:YAG, peelings doux, protocoles anti-taches sécurisés.' },
        { q: 'Quels sont vos horaires et comment prendre rendez-vous ?', a: 'Mardi au dimanche de 10h à 20h (fermé le lundi). Prenez rendez-vous via Planity ou par téléphone au 06 45 23 00 34.' },
        { q: 'Proposez-vous des formules avantageuses ?', a: 'Oui, plusieurs formules combinées : Découverte Visage (110 €), Peau Neuve (110 €), Beauté Express (65 €), Corps Sculpté (60 €), et forfaits épilation.' },
        { q: 'Combien coûtent les soins chez Body by CC ?', a: 'Soin visage à partir de 35 €, pressothérapie 35 €, blanchiment dentaire à partir de 50 €, massage à partir de 25 €.' },
      ];

      const script = document.createElement('script');
      script.id = 'ville-faqpage-jsonld';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqQuestions.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.a,
          },
        })),
      });
      document.head.appendChild(script);

      return () => {
        const el = document.getElementById('ville-faqpage-jsonld');
        if (el) el.remove();
      };
    }
  }, [city, slug]);

  if (!city) return <Navigate to="/404" replace />;

  const otherCities = cities.filter((c) => c.slug !== slug);
  const recentArticles = blogArticles.slice(0, 4);

  const stats = [
    { value: '4.5/5', label: 'Note Google', icon: Star },
    { value: city.transport.distance, label: `de ${city.name}`, icon: Navigation },
    { value: city.transport.duration.replace(' en voiture', ''), label: 'En voiture', icon: Car },
    { value: '6j/7', label: 'Ouvert', icon: Clock },
  ];

  const whyChoose = [
    { icon: Shield, title: 'Expertise médicale', desc: `Carine Tchuente, infirmière diplômée, allie rigueur clinique et savoir-faire esthétique. Une expertise rare en Seine-Saint-Denis, à seulement ${city.transport.distance} de ${city.name}.` },
    { icon: Users, title: 'Spécialiste peaux noires & métissées', desc: `Body by CC est l'un des rares instituts du 93 à proposer des protocoles spécifiquement calibrés pour les phototypes foncés (III à VI). Laser Nd:YAG, peelings adaptés, anti-taches sécurisés — à ${city.transport.duration} de ${city.name}.` },
    { icon: Sparkles, title: 'Technologies de pointe', desc: 'HIFU lifting sans chirurgie, Skincleantech 3-en-1, photothérapie LED, lipolaser dernière génération. Des résultats visibles dès la 1ère séance.' },
    { icon: Star, title: 'Approche personnalisée', desc: 'Chaque soin commence par un diagnostic de peau complet. Protocoles sur-mesure adaptés à votre carnation, votre type de peau et vos objectifs.' },
  ];

  const faqItems = [
    {
      q: `Quels soins proposez-vous près de ${city.name} ?`,
      a: (
        <div className="space-y-3">
          <p>Body by CC propose une gamme complète de soins à seulement <strong>{city.transport.distance}</strong> de {city.name} ({city.transport.duration}) :</p>
          <ul className="space-y-2">
            <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span><strong>Soins visage</strong> — hydratation, anti-âge, peeling, LED, Skincleantech</span></li>
            <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span><strong>HIFU</strong> — lifting sans chirurgie, visage et corps</span></li>
            <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span><strong>Épilation</strong> — laser Nd:YAG (peaux noires), cire, électrolyse définitive</span></li>
            <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span><strong>Soins corps</strong> — pressothérapie, lipolaser, massages</span></li>
            <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span><strong>Beauté</strong> — manucure, blanchiment dentaire, spray tan</span></li>
          </ul>
        </div>
      ),
    },
    {
      q: `Comment venir depuis ${city.name} ?`,
      a: (
        <div className="space-y-3">
          <p>Body by CC est situé au <strong>65 Boulevard Westinghouse, 93270 Sevran</strong>, à seulement <strong>{city.transport.distance}</strong> de {city.name} :</p>
          <ul className="space-y-2">
            <li className="flex items-start gap-2.5"><Car size={15} className="text-primary mt-0.5 shrink-0" /><span><strong>En voiture ({city.transport.duration})</strong> — {city.transport.car}</span></li>
            {city.transport.rer && (
              <li className="flex items-start gap-2.5"><Train size={15} className="text-primary mt-0.5 shrink-0" /><span><strong>En RER</strong> — {city.transport.rer}</span></li>
            )}
            {city.transport.bus && (
              <li className="flex items-start gap-2.5"><Bus size={15} className="text-primary mt-0.5 shrink-0" /><span><strong>En bus</strong> — {city.transport.bus}</span></li>
            )}
          </ul>
          {city.transport.parking && (
            <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground/80 italic">
              <ParkingCircle size={14} className="shrink-0" /> {city.transport.parking}
            </div>
          )}
        </div>
      ),
    },
    {
      q: `Les soins sont-ils adaptés aux peaux noires et métissées ?`,
      a: (
        <div className="space-y-3">
          <p><strong>Oui, c'est notre spécialité.</strong> Carine Tchuente maîtrise les spécificités des peaux à phototypes foncés : laser Nd:YAG pour l'épilation, peelings doux (acide mandélique, kojique), protocoles anti-taches sécurisés.</p>
          <p className="text-sm italic text-muted-foreground/80">Nous accueillons une large clientèle de {city.name} et du 93 avec toutes les carnations.</p>
        </div>
      ),
    },
    {
      q: `Quels sont vos horaires et comment prendre rendez-vous ?`,
      a: (
        <div className="space-y-3">
          <div className="flex items-center gap-3 rounded-xl bg-accent/10 border border-accent/20 p-3">
            <Clock size={16} className="text-accent shrink-0" />
            <p className="text-sm"><strong>Mardi → Dimanche : 10h - 20h</strong> (fermé le lundi)</p>
          </div>
          <p>Prenez rendez-vous par téléphone ou directement à l'institut. Nous vous conseillons de réserver à l'avance pour les soins techniques (HIFU, laser).</p>
        </div>
      ),
    },
    {
      q: `Proposez-vous des formules avantageuses ?`,
      a: (
        <div className="space-y-3">
          <p><strong>Oui</strong>, plusieurs formules combinées à tarif préférentiel :</p>
          <div className="flex flex-wrap gap-2">
            {['Découverte Visage', 'Peau Neuve', 'Beauté Express', 'Corps Sculpté'].map((f) => (
              <span key={f} className="px-3 py-1.5 rounded-full bg-primary/5 border border-primary/10 text-sm">{f}</span>
            ))}
          </div>
          <p className="text-sm text-muted-foreground">+ Forfaits épilation laser multi-séances disponibles.</p>
          <Link to="/formules" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-1.5 text-primary font-heading font-medium text-base hover:underline">
            Découvrir nos formules <ArrowRight size={14} />
          </Link>
        </div>
      ),
    },
    {
      q: `Combien coûtent les soins chez Body by CC ?`,
      a: (
        <div className="space-y-3">
          <p>Nos tarifs sont <strong>accessibles et transparents</strong>. Quelques exemples :</p>
          <ul className="space-y-2">
            <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span>Soin visage à partir de <strong>35 €</strong></span></li>
            <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span>Pressothérapie : <strong>35 €</strong> la séance</span></li>
            <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span>Blanchiment dentaire à partir de <strong>50 €</strong></span></li>
            <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span>Massage à partir de <strong>25 €</strong></span></li>
          </ul>
          <Link to="/services" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-1.5 text-primary font-heading font-medium text-base hover:underline">
            Voir tous nos tarifs <ArrowRight size={14} />
          </Link>
        </div>
      ),
    },
  ];

  return (
    <>
      {/* Hero */}
      <section className="relative bg-petrol py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-petrol via-petrol to-petrol/80" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-petrol-foreground/50 text-sm font-body mb-2">
              <Link to="/" className="hover:text-petrol-foreground transition-colors">Accueil</Link>
              {' › '}
              <span>Body by CC {city.prefix} {city.name}</span>
            </p>
            <h1 className="font-heading text-3xl md:text-5xl font-black text-petrol-foreground leading-tight mb-6">
              Institut beauté & bien-être<br />{city.prefix} {city.name}
            </h1>
            <p className="text-petrol-foreground/70 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-4 font-body">
              Soins du visage, épilation laser, HIFU, pressothérapie et esthétique médicale. Body by CC accueille les clientes de {city.name} dans son institut à Sevran (93).
            </p>
            <p className="text-petrol-foreground/50 text-sm font-body mb-8 flex items-center justify-center gap-2">
              <Route size={14} />
              <span>À seulement <strong className="text-petrol-foreground/70">{city.transport.distance}</strong> — <strong className="text-petrol-foreground/70">{city.transport.duration}</strong></span>
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <a href={PLANITY_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary text-primary-foreground font-heading font-medium text-base hover:bg-primary/90 transition-colors">
                Prendre RDV <ArrowRight size={16} />
              </a>
              <a href="tel:+33645230034" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-petrol-foreground/20 text-petrol-foreground font-heading font-medium text-base hover:bg-petrol-foreground/10 transition-colors">
                <Phone size={16} /> 06 45 23 00 34
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="px-6 lg:px-8 -mt-8 relative z-10 pb-8">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              className="flex flex-col items-center gap-2 p-5 rounded-2xl border border-border bg-card shadow-lg text-center"
            >
              <stat.icon size={20} className="text-primary" />
              <p className="font-heading font-bold text-2xl text-foreground">{stat.value}</p>
              <p className="text-xs text-muted-foreground font-body">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Contexte local */}
      <section className="py-12 md:py-16 px-6 lg:px-8 bg-secondary/30">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="p-6 md:p-8 rounded-2xl border border-primary/20 bg-card">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <MapPin size={24} className="text-primary" />
              </div>
              <div className="space-y-3">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground">
                  Body by CC, votre institut beauté près de {city.name}
                </h2>
                <p className="text-muted-foreground leading-relaxed font-body">{city.localContext}</p>
                {city.nearbyLandmarks.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-2">
                    {city.nearbyLandmarks.map((l) => (
                      <span key={l} className="px-3 py-1.5 rounded-full bg-accent/10 border border-accent/20 text-xs font-body text-foreground">{l}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Comment venir — section dédiée */}
      <section className="py-16 md:py-20 px-6 lg:px-8 bg-background">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-heading font-medium uppercase tracking-[0.2em] text-accent mb-3">Accès</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
              Comment venir depuis {city.name} ?
            </h2>
            <p className="text-muted-foreground mt-3 max-w-2xl mx-auto font-body">
              Body by CC est à seulement <strong>{city.transport.distance}</strong> de {city.name}. Voici comment nous rejoindre facilement.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }}
              className="p-6 rounded-2xl border border-border bg-card">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Car size={20} className="text-primary" />
              </div>
              <h3 className="font-heading font-bold text-foreground mb-2">En voiture</h3>
              <p className="text-sm text-muted-foreground font-body leading-relaxed mb-3">{city.transport.car}</p>
              <div className="flex items-center gap-2 text-sm font-heading font-semibold text-accent">
                <Clock size={14} /> {city.transport.duration}
              </div>
            </motion.div>

            {city.transport.rer && (
              <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.1 }}
                className="p-6 rounded-2xl border border-border bg-card">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <Train size={20} className="text-primary" />
                </div>
                <h3 className="font-heading font-bold text-foreground mb-2">En RER / Métro</h3>
                <p className="text-sm text-muted-foreground font-body leading-relaxed">{city.transport.rer}</p>
              </motion.div>
            )}

            {city.transport.bus && (
              <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.2 }}
                className="p-6 rounded-2xl border border-border bg-card">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <Bus size={20} className="text-primary" />
                </div>
                <h3 className="font-heading font-bold text-foreground mb-2">En bus</h3>
                <p className="text-sm text-muted-foreground font-body leading-relaxed">{city.transport.bus}</p>
              </motion.div>
            )}
          </div>

          {city.transport.parking && (
            <div className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground font-body">
              <ParkingCircle size={16} className="text-primary" />
              <span>{city.transport.parking}</span>
            </div>
          )}
        </div>
      </section>

      {/* Pourquoi choisir Body by CC */}
      <section className="py-16 md:py-20 px-6 lg:px-8 bg-card">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-heading font-medium uppercase tracking-[0.2em] text-accent mb-3">Notre expertise</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
              Pourquoi choisir Body by CC près de {city.name} ?
            </h2>
            <p className="text-muted-foreground mt-3 max-w-2xl mx-auto font-body">
              Un institut de beauté alliant expertise médicale, technologies de pointe et spécialisation peaux noires & métissées — à seulement {city.transport.distance} de {city.name}.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {whyChoose.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="flex items-start gap-4 p-6 rounded-2xl border border-border bg-background"
              >
                <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <item.icon size={20} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-foreground mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed font-body">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Nos soins */}
      <section className="py-16 md:py-20 px-6 lg:px-8 bg-background">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-heading font-medium uppercase tracking-[0.2em] text-accent mb-3">Nos prestations</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
              Nos soins accessibles depuis {city.name}
            </h2>
            <p className="text-muted-foreground mt-3 max-w-2xl mx-auto font-body">
              Retrouvez l'ensemble de nos prestations à seulement {city.transport.distance} de chez vous — {city.transport.duration}.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.slice(0, 8).map((service, i) => {
              const Icon = service.icon;
              return (
                <motion.div key={service.slug} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}>
                  <Link to={`/services/${service.slug}`} onClick={() => window.scrollTo(0, 0)} className="block p-6 rounded-2xl border border-border bg-card hover:shadow-lg hover:border-primary/20 transition-all group h-full">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                      <Icon size={20} className="text-primary" />
                    </div>
                    <h3 className="font-heading font-bold text-foreground mb-2">{service.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 font-body">{service.shortDesc}</p>
                  </Link>
                </motion.div>
              );
            })}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/services"
              onClick={() => window.scrollTo(0, 0)}
              className="inline-flex items-center gap-2 text-primary font-heading font-medium text-base hover:underline"
            >
              Voir tous nos soins <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Contact & map */}
      <ContactSection />

      {/* CTA */}
      <CTASection
        title={`Un soin beauté près de ${city.name} ?`}
        subtitle={`Prenez rendez-vous chez Body by CC, à seulement ${city.transport.distance} de ${city.name} (${city.transport.duration}). Expertise médicale, technologies de pointe et spécialisation peaux noires & métissées.`}
        buttonLabel="Prendre rendez-vous"
        buttonLink={PLANITY_URL}
        isExternal
      />

      {/* FAQ */}
      <section className="py-16 md:py-20 px-6 lg:px-8 bg-background">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-heading font-medium uppercase tracking-[0.2em] text-accent mb-3">FAQ</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
              Questions fréquentes — {city.name}
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqItems.map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border border-border rounded-2xl px-6 md:px-8 bg-card data-[state=open]:border-primary/30 data-[state=open]:shadow-sm transition-all">
                <AccordionTrigger className="font-heading text-base md:text-lg font-semibold text-foreground hover:no-underline py-5">{item.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-6 font-body">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-8">
            <Link
              to="/faq"
              onClick={() => window.scrollTo(0, 0)}
              className="inline-flex items-center gap-2 text-primary font-heading font-medium text-base hover:underline"
            >
              Voir toutes les questions <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Blog */}
      {recentArticles.length > 0 && (
        <section className="py-16 md:py-20 px-6 lg:px-8 bg-secondary/30">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-xs font-heading font-medium uppercase tracking-[0.2em] text-accent mb-3">Nos conseils</p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
                Nos derniers articles beauté
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {recentArticles.map((article) => (
                <BlogCard key={article.slug} article={article} />
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/blog"
                onClick={() => window.scrollTo(0, 0)}
                className="inline-flex items-center gap-2 text-primary font-heading font-medium text-base hover:underline"
              >
                Voir tous les articles <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Villes voisines */}
      <section className="py-16 md:py-20 px-6 lg:px-8 bg-background">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground mb-3">
            Nous accueillons aussi les clientes de
          </h2>
          <p className="text-muted-foreground mb-8 font-body">
            Body by CC est facilement accessible depuis toutes les villes de Seine-Saint-Denis.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {otherCities.map((c) => (
              <Link key={c.slug} to={`/ville/${c.slug}`} onClick={() => window.scrollTo(0, 0)} className="flex items-center justify-between gap-3 px-5 py-4 rounded-xl border border-border bg-card hover:shadow-md hover:border-primary/20 transition-all group">
                <div className="flex items-center gap-3">
                  <MapPin size={16} className="text-primary shrink-0" />
                  <span className="text-sm font-heading font-medium text-foreground group-hover:text-primary transition-colors">{c.name}</span>
                </div>
                <span className="text-xs text-muted-foreground font-body">{c.transport.distance}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default VilleDetail;
